import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useLocation, useNavigate } from 'react-router-dom';

import useAuth from '../../../../hooks/useAuth';
import { transformData } from '../helpers/getDemandsData';
import { PATH_PAGE } from '../../../../routes/paths';
import { useDispatch, useSelector } from '../../../../redux/store';
import { cancelDemand, getDemands, onSelectDemand } from '../../../../redux/slices/demands';
import { deleteDemand } from '../../../../redux/slices/partners';
import CloseButton from '../../clockings/ClockingButtons/ClockingButton/CloseButton';

/**
 * Custom hook to manage demands in the system. It integrates functionalities for handling demand operations,
 * such as retrieving, creating, and cancelling demands, with specific handling based on user roles and states.
 *
 * @param {Function} handleReloadFiles - Function to reload files after certain operations.
 * @returns {Object} - The various handlers and states related to demand management.
 */
const useDemands = (handleReloadFiles) => {
  const {
    env: {
      parameters: { currency_name: currencyName }
    },
    features,
    isPartner,
    isResource,
    user: currentUser
  } = useAuth();
  const isMountedRef = useRef(true);
  const dispatch = useDispatch();
  const { isLoading, demandId, demands, error } = useSelector((state) => state.demands);
  const { resource } = useSelector((state) => state.resources);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();

  const [demandType, setDemandType] = useState({});
  const [openModalConfirmCancellation, setOpenModalConfirmCancellation] = useState(false);
  const [openModalNewDemand, setOpenModalNewDemand] = useState(false);

  const {
    manager_partner: { partnerDemands: partnerDemandsPath },
    resource: { demands: demandsPath }
  } = PATH_PAGE;

  const { id: userId, userResourceId, clientUserId } = currentUser || {};
  const receiverUser = isPartner ? currentUser : resource;

  const conversations = useMemo(
    () => transformData(userId, receiverUser, demands, features, isPartner, isResource, currencyName),
    [demands, features, resource, userId]
  );

  useEffect(() => () => (isMountedRef.current = false), []); // cleanup only

  // Open demand creation modal
  const handleOpenModalNewDemand = useCallback((demandType = null) => {
    if (demandType) {
      setDemandType(demandType);
    }
    setOpenModalNewDemand(true);
  }, []);

  // Close demand creation modal
  const handleCloseModalNewDemand = useCallback(() => {
    setOpenModalNewDemand(false);
  }, []);

  // Open modal that ask a confirmation to cancel a demand
  const handleOpenModalConfirmCancellation = useCallback(
    (e) => {
      e.stopPropagation(); // Prevent the parent event to fire (ex: TableDocument / TableRow / Action)
      if (isResource) {
        dispatch(onSelectDemand(demandId));
      }
      setOpenModalConfirmCancellation(true);
    },
    [demandId, dispatch]
  );

  // Close modal that ask a confirmation to cancel a demand
  const handleCloseModalConfirmCancellation = useCallback(() => {
    setOpenModalConfirmCancellation(false);
  }, []);

  // Dispatch the current selected demand
  const handleSelectDemand = useCallback((id) => {
    dispatch(onSelectDemand(id));
  }, []);

  const handleCancelCallback = useCallback(
    (error, path) => {
      // Close cancel confirmation modal
      setOpenModalConfirmCancellation(false);
      if (path) {
        navigate(path);
      } else {
        handleReloadFiles();
      }
      if (error) {
        enqueueSnackbar("Votre demande n'a pas pu être annulée", {
          variant: 'error',
          action: (key) => <CloseButton handleClick={() => closeSnackbar(key)} />
        });
      } else {
        enqueueSnackbar('Demande annulée avec succès', {
          variant: 'success',
          action: (key) => <CloseButton handleClick={() => closeSnackbar(key)} />
        });
      }
    },
    [isMountedRef]
  );

  // DemandPage cancellation
  const handleConfirmDemandCancellation = useCallback(
    async (e, demandId, fileId) => {
      e.stopPropagation();

      let path = '';
      if (location.pathname.includes('demand')) {
        path = isPartner ? partnerDemandsPath : demandsPath;
      }

      if (fileId) {
        // Remove a file from a partner demand
        dispatch(deleteDemand({ partnerDemandId: demandId, partnerFileDemandId: fileId }, handleCancelCallback));
      } else {
        // Cancel a whole demand
        dispatch(cancelDemand({ demandId, isPartner }, (err) => handleCancelCallback(err, path)));
      }
    },
    [dispatch, enqueueSnackbar, navigate, setOpenModalConfirmCancellation, isMountedRef, isPartner]
  );

  const handleLoadData = useCallback(
    (reload, demandId = null) => {
      dispatch(
        getDemands({ resourceId: userResourceId, clientUserId: isPartner ? clientUserId : null, demandId }, reload)
      );
    },
    [dispatch, userResourceId, clientUserId, isPartner]
  );

  const handleRefresh = (event) => {
    handleLoadData(true);
    event.detail.complete();
  };

  return {
    conversations,
    demands,
    demandId,
    demandType,
    error,
    isLoading,
    openModalConfirmCancellation,
    openModalNewDemand,
    userResourceId,
    handleConfirmDemandCancellation,
    handleLoadData,
    handleOpenModalConfirmCancellation,
    handleCloseModalConfirmCancellation,
    handleOpenModalNewDemand,
    handleCloseModalNewDemand,
    handleRefresh,
    handleSelectDemand,
    onSelectDemand
  };
};

export default useDemands;
